import { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import _ from 'lodash/collection';
import classNames from 'classnames';

import { useGlobalContext } from '../../context/GlobalContext';

import serviceService from '../../services/serviceService';
import userService from '../../services/userService';
import auth from '../../services/authService';

import { scrollTop, setDocTitle } from '../../utils/utilities';
import { getSetting, setSetting, settingKeys } from '../../utils/settings';
import { replaceAccents } from '../../utils/string';
import { removeColorTheme } from '../../utils/colorTheme';
import { sortSongsByPopularity, getPopularSongs } from '../../helpers/songStats';
import { isNewSong, wasContentUpdated } from '../../helpers/songDates';
import {
  buildServicePlaysBySong,
  getChartFilterOptions,
  getKeyFilterOptions,
  getServiceNameOptions,
  getThemeFilterOptions,
  getTimeSignatureFilterOptions,
  matchesCharts,
  matchesKeys,
  matchesThemes,
  matchesTimeSignatures,
  orderByCharted,
  resolveServicePlay,
} from '../../helpers/songFilters';
import { getSelectedTagAccentVar } from '../../helpers/tagColors';
import { mergeSuggestedSongs } from '../../helpers/suggestedSongs';
import songService from '../../services/songService';

import Services from '../../components/Services';
import SongList from '../../components/SongList';
import SongsMenu from '../../components/SongsMenu';
import SearchBox from '../../components/common/SearchBox';
import TagGroup from '../../components/common/TagGroup';
import PromptModal from '../../components/modals/PromptModal';
import LoadMoreFooter from '../../components/common/LoadMoreFooter';
import Modal from '../../components/common/Modal';
import AccountRemovedModal from '../../components/modals/AccountRemovedModal';
import SuggestSongsModal from '../../components/modals/SuggestSongsModal';
import SongFiltersModal from '../../components/modals/SongFiltersModal';
import UpgradeBanner from '../../components/subscription/UpgradeBanner';
import UpgradeModal from '../../components/subscription/UpgradeModal';
import { hasReachedLimit, SUBSCRIPTION_ENABLED } from '../../utils/subscription';

import { ReactComponent as FilterIcon } from '../../icons/filter-list.svg';
import { ReactComponent as MusicIcon } from '../../icons/music-double-note.svg';
import Icon from '../../components/common/Icon';

import './Main.scss';

const NO_EXCLUSIONS = [];

const Main = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [highlightedSongId] = useState(() => location.state?.highlightedSongId);

  useEffect(() => {
    // Reset the highlighted song, so it only shows once
    if (highlightedSongId) {
      history.replace(location.pathname, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageSize = 50;
  const user = auth.getCurrentUser();
  const {
    showRemovedFromTeamModal,
    setShowRemovedFromTeamModal,
    canSuggestSongs,
    songs,
    setSongs,
    services,
    setServices,
    categories,
    dataLoaded,
    servicesLoaded,
    currentServiceForm,
    setCurrentServiceForm,
    allThemes,
  } = useGlobalContext();

  const [itemsToShow, setItemsToShow] = useState(50);
  const [categoryId, setCategoryId] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedThemes, setSelectedThemes] = useState([]);
  const [sortColumn, setSortColumn] = useState({ path: 'title', order: 'none' });
  const [popularitySort, setPopularitySort] = useState('none'); // 'none', 'most', 'least', 'newest', 'oldest', 'updated'
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedTimeSignatures, setSelectedTimeSignatures] = useState([]);
  const [selectedCharts, setSelectedCharts] = useState([]);
  const [excludedServiceNames, setExcludedServiceNames] = useState(
    () => getSetting(settingKeys.songFilterExcludedServices) || [],
  );
  const [showFilterModal, setShowFilterModal] = useState(false);
  const loadingSongs = !dataLoaded;
  const loadingServices = !servicesLoaded;
  const [hideServicesContainer, setHideServicesContainer] = useState(false);
  const [modalDeleteSong, setModalDeleteSong] = useState(null);
  const [modalDeleteService, setModalDeleteService] = useState(null);
  const [isDeleteSongModalOpen, setIsDeleteSongModalOpen] = useState(false);
  const [isDeleteServiceModalOpen, setIsDeleteServiceModalOpen] = useState(false);
  // A service about to be edited while suggested songs are loaded, held until the admin picks how
  const [serviceToEditWithSuggestions, setServiceToEditWithSuggestions] = useState(null);
  const [suggestedSongs, setSuggestedSongs] = useState([]);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [hasSuggestedSongsFromUrl, setHasSuggestedSongsFromUrl] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const limitReached = SUBSCRIPTION_ENABLED && dataLoaded && hasReachedLimit(songs);
  const hideNewSong = !dataLoaded || limitReached;

  const songsInServiceIds = useMemo(() => {
    const ids = new Set(services.flatMap((s) => s.songsIds));

    currentServiceForm?.songs?.forEach((song) => ids.add(song._id));
    currentServiceForm?.newSongs?.forEach((song) => ids.add(song._id));

    return ids;
  }, [services, currentServiceForm?.songs, currentServiceForm?.newSongs]);

  // Every filter the list applies except the modal's key, time signature and theme — category and search.
  // The chips are built from this rather than from the whole library, so their counts
  // describe the songs actually in front of you: under Worship, a C chip reading 4 means four worship
  // songs in C, and picking C lands you exactly those four rows. Each of the three is counted under the
  // others, so a 3/4 pick narrows the key and theme counts too.
  const songsInScope = useMemo(() => {
    const currentUser = auth.getCurrentUser();
    const isAdmin = currentUser && currentUser.isAdmin;
    let filtered = songs;

    // Handle special categories first
    if (categoryId === 'favorites') {
      filtered = songs.filter((song) => song.favorite === true);
    } else if (categoryId === 'new') {
      // The same test the NEW badge is drawn from, so the pill collects exactly the songs
      // wearing that badge and nothing else.
      filtered = songs.filter((song) => isNewSong(song.createdAt, song.isNewSong));

      if (!isAdmin) filtered = filtered.filter((song) => !song.isHidden);
    } else if (categoryId === 'hidden') {
      filtered = songs.filter((song) => song.isHidden === true);
    } else if (categoryId === 'drafts') {
      filtered = songs.filter((song) => song.isDraft === true);
    } else {
      // For all other categories, filter out hidden songs for non-admin users
      if (!isAdmin) filtered = filtered.filter((song) => !song.isHidden);

      // Apply category filter if not 'all'
      if (categoryId && categoryId !== 'all') {
        filtered = filtered.filter((song) => song.category._id === categoryId);
      }
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((song) => {
        const songTitle = replaceAccents(song.title.toLowerCase());
        const songArtist = replaceAccents(song.artist.toLowerCase());
        const query = replaceAccents(searchQuery.toLowerCase());

        return songTitle.includes(query) || songArtist.includes(query);
      });
    }

    return filtered;
  }, [songs, categoryId, searchQuery]);

  // Each panel counts the songs the other filters leave in view
  const keyFilterOptions = useMemo(
    () =>
      getKeyFilterOptions(
        songsInScope.filter(
          (song) =>
            matchesTimeSignatures(song, selectedTimeSignatures) &&
            matchesThemes(song, selectedThemes) &&
            matchesCharts(song, selectedCharts),
        ),
      ),
    [songsInScope, selectedTimeSignatures, selectedThemes, selectedCharts],
  );
  const timeSignatureOptions = useMemo(
    () =>
      getTimeSignatureFilterOptions(
        songsInScope.filter(
          (song) =>
            matchesKeys(song, selectedKeys) &&
            matchesThemes(song, selectedThemes) &&
            matchesCharts(song, selectedCharts),
        ),
      ),
    [songsInScope, selectedKeys, selectedThemes, selectedCharts],
  );
  const themeOptions = useMemo(
    () =>
      getThemeFilterOptions(
        songsInScope.filter(
          (song) =>
            matchesKeys(song, selectedKeys) &&
            matchesTimeSignatures(song, selectedTimeSignatures) &&
            matchesCharts(song, selectedCharts),
        ),
        allThemes,
      ),
    [songsInScope, selectedKeys, selectedTimeSignatures, selectedCharts, allThemes],
  );
  const chartOptions = useMemo(
    () =>
      getChartFilterOptions(
        songsInScope.filter(
          (song) =>
            matchesKeys(song, selectedKeys) &&
            matchesTimeSignatures(song, selectedTimeSignatures) &&
            matchesThemes(song, selectedThemes),
        ),
      ),
    [songsInScope, selectedKeys, selectedTimeSignatures, selectedThemes],
  );

  const serviceNameOptions = useMemo(() => getServiceNameOptions(services, songs), [services, songs]);
  // A saved name whose services and songs are all gone no longer means anything
  const activeExcludedServiceNames = useMemo(
    () => excludedServiceNames.filter((id) => serviceNameOptions.some((name) => name.id === id)),
    [excludedServiceNames, serviceNameOptions],
  );
  const playsBySong = useMemo(() => buildServicePlaysBySong(services), [services]);
  // Services are only picked under the added-to-service sorts, so outside them none are left out
  const isServiceDateSort = popularitySort === 'newest' || popularitySort === 'oldest';
  const serviceExclusions = isServiceDateSort ? activeExcludedServiceNames : NO_EXCLUSIONS;
  // The "Added to" label and the added-to-service sorts both read from this, so they only
  // count the service names still ticked in the filter
  const servicePlays = useMemo(
    () => new Map(songsInScope.map((song) => [song._id, resolveServicePlay(song, playsBySong, serviceExclusions)])),
    [songsInScope, playsBySong, serviceExclusions],
  );
  const tickedServiceLabels = useMemo(
    () =>
      serviceExclusions.length > 0
        ? serviceNameOptions.filter((name) => !serviceExclusions.includes(name.id)).map((name) => name.label)
        : null,
    [serviceNameOptions, serviceExclusions],
  );

  const activeFilterCount = [
    popularitySort !== 'none',
    selectedKeys.length > 0,
    selectedTimeSignatures.length > 0,
    selectedThemes.length > 0,
    selectedCharts.length > 0,
    serviceExclusions.length > 0,
  ].filter(Boolean).length;

  // The song list wears the hue of whichever pill is lit, so the filter is legible from the
  // songs themselves and not only from the row above them.
  // Only the label is translated: logic elsewhere matches on the English names ('Praise', 'Worship')
  const localizedCategories = useMemo(
    () =>
      categories.map((category) => ({
        ...category,
        name: t(`categories.${category.name.toLowerCase()}`, { defaultValue: category.name }),
      })),
    [categories, t],
  );

  const categoryAccent = useMemo(() => getSelectedTagAccentVar(categories, categoryId), [categories, categoryId]);

  useEffect(() => {
    setDocTitle(t('songs.title'));
  }, [t]);

  useEffect(() => {
    const initializeComponent = async () => {
      // Check if user has been removed from team first
      const user = auth.getCurrentUser();

      try {
        const { data: hasNoTeam } = await userService.checkUserHasNoTeam(user._id);

        if (hasNoTeam) {
          setShowRemovedFromTeamModal(true);
        }
      } catch (error) {
        // Ignore errors here
      }

      // Songs, services, categories + allThemes are all fetched once in
      // GlobalProvider so the global startup loader can wait on them.
    };

    initializeComponent();
  }, [setShowRemovedFromTeamModal]);

  // Handle suggested songs from URL parameter
  useEffect(() => {
    if (!user.isAdmin || songs.length === 0) return;

    const urlParams = new URLSearchParams(window.location.search);
    const suggestedSongsParam = urlParams.get('suggestedSongs');

    if (suggestedSongsParam && !hasSuggestedSongsFromUrl) {
      const songIds = suggestedSongsParam.split(',').filter((id) => id.trim());

      if (songIds.length > 0) {
        // Find valid songs that exist in the songs list
        const validSongs = songIds
          .map((id) => songs.find((song) => song._id === id.trim()))
          .filter((song) => song !== undefined);

        if (validSongs.length > 0) {
          setCurrentServiceForm((prev) => ({
            ...prev,
            songs: validSongs,
          }));
          setHasSuggestedSongsFromUrl(true);
        }
      }
    }
  }, [songs, user.isAdmin, hasSuggestedSongsFromUrl, setCurrentServiceForm]);

  const handleDelete = async (song) => {
    setModalDeleteSong(song);
    setIsDeleteSongModalOpen(true);
  };

  const deleteSong = async () => {
    // This is set by handleDelete
    const song = modalDeleteSong;

    const originalSongs = [...songs];
    const updatedSongs = originalSongs.filter((s) => s._id !== song._id);
    setSongs(updatedSongs);

    try {
      await songService.deleteSong(song._id);
      // remove song from favorites
      const favSong = originalSongs.find((s) => s._id === song._id);
      if (favSong.favorite) await userService.removeFavoriteSong(song._id);
    } catch (ex) {
      if (ex.response && ex.response.status === 404) toast.error(t('songs.alreadyDeleted'));
      setSongs(originalSongs);
    }
  };

  const handleFavorite = async (song) => {
    const songsData = [...songs];
    const index = songsData.indexOf(song);
    songsData[index] = { ...song };
    // toggle the favorite property
    songsData[index].favorite = !songsData[index].favorite;
    setSongs(songsData);

    if (songsData[index].favorite) {
      await userService.saveFavoriteSong(songsData[index]._id);
    } else {
      await userService.removeFavoriteSong(songsData[index]._id);
    }
  };

  const handleLoadMore = () => {
    setItemsToShow((prev) => prev + pageSize);
  };

  const handleScrollToTop = () => {
    scrollTop();
  };

  const handleCategorySelect = (category) => {
    // Tapping the lit pill again lets it go, back to All
    setCategoryId(category._id === categoryId ? 'all' : category._id);
    setSearchQuery('');
    setItemsToShow(50);
  };

  const handlePopularitySort = (sortType) => {
    setPopularitySort(sortType);
    setSortColumn({ path: 'title', order: 'none' });
    setItemsToShow(50);
  };

  const handleKeysFilter = (keys) => {
    setSelectedKeys(keys);
    setItemsToShow(50);
  };

  const handleTimeSignaturesFilter = (signatures) => {
    setSelectedTimeSignatures(signatures);
    setItemsToShow(50);
  };

  const handleThemesFilter = (themes) => {
    setSelectedThemes(themes);
    setItemsToShow(50);
  };

  const handleChartsFilter = (statuses) => {
    setSelectedCharts(statuses);
    setItemsToShow(50);
  };

  const handleExcludedServiceNames = (names) => {
    setExcludedServiceNames(names);
    setSetting(settingKeys.songFilterExcludedServices, names);
  };

  const handleResetFilters = () => {
    handlePopularitySort('none');
    handleKeysFilter([]);
    handleTimeSignaturesFilter([]);
    handleThemesFilter([]);
    handleChartsFilter([]);
    handleExcludedServiceNames([]);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setCategoryId(null);
    setItemsToShow(50);
  };

  const handleClearInput = () => {
    setSearchQuery('');
    setCategoryId('all');
  };

  // Get paged data function
  const getPagedData = () => {
    const filtered = songsInScope.filter(
      (song) =>
        matchesKeys(song, selectedKeys) &&
        matchesTimeSignatures(song, selectedTimeSignatures) &&
        matchesThemes(song, selectedThemes) &&
        matchesCharts(song, selectedCharts),
    );
    const servicePlayDate = (song) => servicePlays.get(song._id)?.date;

    // sorting
    let sorted = null;

    // Popularity sorting takes precedence
    if (popularitySort === 'most') {
      sorted = sortSongsByPopularity(filtered, 'most');
    } else if (popularitySort === 'least') {
      sorted = sortSongsByPopularity(filtered, 'least');
    } else if (popularitySort === 'newest') {
      // Sort by most recent service play date
      // Songs with dates go first (sorted descending), songs without dates go to bottom
      const songsWithDate = filtered.filter(servicePlayDate);
      const songsWithoutDate = filtered.filter((song) => !servicePlayDate(song));

      const sortedWithDate = _.orderBy(songsWithDate, [(song) => new Date(servicePlayDate(song))], ['desc']);
      sorted = [...sortedWithDate, ...songsWithoutDate];
    } else if (popularitySort === 'oldest') {
      // Sort by oldest service play date
      // Songs without dates go first, then songs with dates (sorted ascending)
      const songsWithDate = filtered.filter(servicePlayDate);
      const songsWithoutDate = filtered.filter((song) => !servicePlayDate(song));
      const sortedWithDate = _.orderBy(songsWithDate, [(song) => new Date(servicePlayDate(song))], ['asc']);

      sorted = [...songsWithoutDate, ...sortedWithDate];
    } else if (popularitySort === 'updated') {
      // Most recently edited first, keying off the same contentUpdatedAt the purple UPDATED
      // badge reads, so the songs wearing that badge are the ones at the top. A song that was
      // never edited since its creation has nothing to sort on and goes to the bottom.
      const songsWithUpdate = filtered.filter((song) => wasContentUpdated(song.createdAt, song.contentUpdatedAt));
      const songsWithoutUpdate = filtered.filter((song) => !wasContentUpdated(song.createdAt, song.contentUpdatedAt));
      const sortedWithUpdate = _.orderBy(songsWithUpdate, [(song) => song.contentUpdatedAt], ['desc']);

      sorted = [...sortedWithUpdate, ...songsWithoutUpdate];
    } else if (sortColumn.order !== 'none') {
      sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);
    } else {
      sorted = _.orderBy(filtered, 'createdAt', 'desc');
    }

    // Filtering by chart puts the fully charted first, then the most charted, each kept in the sort above
    if (selectedCharts.length > 0) sorted = orderByCharted(sorted);

    // Show only itemsToShow instead of paginating
    const displayedSongs = sorted.slice(0, itemsToShow);
    const remainingSongs = Math.max(0, sorted.length - itemsToShow);

    // Calculate popularity data for the badges
    const { maxPlays, popularSongs, mostPlayedOnly } = getPopularSongs(songs);
    const popularityData = { maxPlays, popularSongs, mostPlayedOnly };

    return {
      totalCount: filtered.length,
      data: displayedSongs,
      popularityData,
      remainingSongs,
      displayedCount: displayedSongs.length,
    };
  };

  const toggleCreateService = () => {
    // Only reset if there's no service in progress
    // If there are already songs, we're continuing an existing service creation
    if (currentServiceForm.songs.length === 0 && !currentServiceForm.title && !currentServiceForm.editServiceId) {
      setCurrentServiceForm({
        songs: [],
        newSongs: [],
        title: '',
        date: new Date(),
        editServiceId: null,
      });
    }
    scrollTop();
    history.push('/services/new');
  };

  // The picked-songs pill's ✕: drops the service in progress without opening it
  const discardServiceInProgress = () => {
    setCurrentServiceForm({
      songs: [],
      newSongs: [],
      title: '',
      date: new Date(),
      editServiceId: null,
    });
    // Songs brought in from a suggestion link would come back on a reload while the param is there
    if (hasSuggestedSongsFromUrl) {
      setHasSuggestedSongsFromUrl(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleNewSong = () => {
    if (limitReached) return;

    const uniqueArtists = [...new Set(songs.map((song) => song.artist))].sort();

    history.push('/songs/new', {
      artists: uniqueArtists,
      from: '/songs',
    });
  };

  const handleEditSong = (song) => {
    const uniqueArtists = [...new Set(songs.map((song) => song.artist))].sort();

    history.push(`/songs/${song._id}`, {
      artists: uniqueArtists,
      from: '/songs',
    });
  };

  const handleAddToService = (song) => {
    setCurrentServiceForm((prev) => {
      let currentServiceData = [...prev.songs];
      let currentNewSongs = [...(prev.newSongs || [])];

      // check if the song has already been added to the service
      const songFound = currentServiceData.find((s) => s._id === song._id);

      if (!songFound) {
        currentServiceData.push(song);

        // Track as newly added when editing an existing service
        if (prev.editServiceId) {
          currentNewSongs.push(song);
        }
      } else {
        // Delete song if it has already been added.
        const index = currentServiceData.indexOf(songFound);

        currentServiceData.splice(index, 1);

        // Remove from newSongs if it was there
        if (prev.editServiceId) {
          currentNewSongs = currentNewSongs.filter((s) => s._id !== song._id);
        }
      }

      return {
        ...prev,
        songs: currentServiceData,
        newSongs: currentNewSongs,
      };
    });
  };

  const handleAddToSuggested = (song) => {
    let suggestedSongsData = [...suggestedSongs];
    // Check if the song has already been added to suggested songs
    const songFound = suggestedSongsData.find((s) => s._id === song._id);

    if (!songFound) {
      suggestedSongsData.push(song);
    } else {
      // delete song if it has already been added.
      const index = suggestedSongsData.indexOf(songFound);
      suggestedSongsData.splice(index, 1);
    }

    setSuggestedSongs(suggestedSongsData);
  };

  const handleOpenSuggestModal = () => {
    if (suggestedSongs.length > 0) {
      setShowSuggestModal(true);
    }
  };

  const handleCloseSuggestModal = () => {
    setShowSuggestModal(false);
  };

  const handleClearSuggestedSongs = () => {
    setSuggestedSongs([]);
    setShowSuggestModal(false);
  };

  const handleSuggestedSongsReorder = useCallback((oldIndex, newIndex) => {
    setSuggestedSongs((prevSongs) => {
      const reorderedSongs = [...prevSongs];
      const [movedSong] = reorderedSongs.splice(oldIndex, 1);

      reorderedSongs.splice(newIndex, 0, movedSong);

      return reorderedSongs;
    });
  }, []);

  const handleViewService = (service) => {
    history.push(`/view/service/${service._id}`);
  };

  const handleDeleteService = (service) => {
    setModalDeleteService(service);
    setIsDeleteServiceModalOpen(true);
  };

  const deleteService = async () => {
    // this is set by handleDeleteService
    const service = modalDeleteService;

    const originalServices = [...services];
    const updatedServices = originalServices.filter((s) => s._id !== service._id);

    try {
      setServices(updatedServices);
      await serviceService.deleteService(service._id);
    } catch (ex) {
      if (ex.response && ex.response.status === 404) {
        toast.error(t('services.alreadyDeleted'));
      }
      setServices(originalServices);
    }
  };

  const hasLoadedSuggestions = hasSuggestedSongsFromUrl && currentServiceForm.songs.length > 0;

  // Opens the service in the form, with the suggested songs either replacing its songs or after them
  const editServiceWithSuggestions = (service, replace) => {
    const { songs: formSongs, newSongs } = mergeSuggestedSongs(
      getCurrenServiceSongs(service.songsIds),
      currentServiceForm.songs,
      replace,
    );

    setCurrentServiceForm({
      songs: formSongs,
      newSongs,
      title: service.title,
      date: new Date(service.date),
      editServiceId: service._id,
    });
    setHasSuggestedSongsFromUrl(false);
    setServiceToEditWithSuggestions(null);
    // Clear the URL param
    window.history.replaceState({}, document.title, window.location.pathname);
    history.push(`/services/${service._id}`);
  };

  const handleEditService = (service) => {
    const currentServiceData = getCurrenServiceSongs(service.songsIds);

    if (hasLoadedSuggestions) {
      // A service with songs of its own asks whether to empty it first; an empty one just takes them
      if (currentServiceData.length > 0) setServiceToEditWithSuggestions(service);
      else editServiceWithSuggestions(service, true);
      return;
    }

    setCurrentServiceForm({
      songs: currentServiceData,
      newSongs: [],
      title: service.title,
      date: new Date(service.date),
      editServiceId: service._id,
    });
    history.push(`/services/${service._id}`);
  };

  const getCurrenServiceSongs = (songsIds) => {
    let currentServiceSongs = [];

    for (let i = 0; i < songsIds.length; i++) {
      currentServiceSongs.push(songs.find((s) => s._id === songsIds[i]));
    }

    return currentServiceSongs;
  };

  const hideServices = (hide) => {
    setHideServicesContainer(hide);
  };

  const handleShowServicesIconClick = () => {
    if (hideServicesContainer) {
      hideServices(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const { totalCount, data: pagedSongs, popularityData, remainingSongs, displayedCount } = getPagedData();

  const handleLogout = () => {
    auth.signout();
    removeColorTheme();
    window.location = '/';
  };

  // If user has been removed from team, only show the modal
  if (showRemovedFromTeamModal) {
    return (
      <Modal
        isOpen={showRemovedFromTeamModal}
        onClose={handleLogout}
        title={t('songs.accountRemoved')}
        showCloseButton={false}
        closeOnBackdropClick={false}
        opaqueBackdrop={true}
        showLogo={true}
      >
        <AccountRemovedModal />
      </Modal>
    );
  }

  return (
    <>
      {(user.isAdmin || canSuggestSongs) && (
        <SongsMenu
          pickedCount={currentServiceForm.songs.length}
          serviceTitle={currentServiceForm.title}
          onCreateService={toggleCreateService}
          onNewSong={handleNewSong}
          suggestedCount={suggestedSongs.length}
          onSuggestSongs={handleOpenSuggestModal}
          showSuggestButton={!user.isAdmin && canSuggestSongs}
          hasSuggestedSongs={hasSuggestedSongsFromUrl}
          hideNewSong={hideNewSong}
          onDiscard={!user.isAdmin && canSuggestSongs ? handleClearSuggestedSongs : discardServiceInProgress}
        />
      )}
      <div className="mg-container Main">
        {SUBSCRIPTION_ENABLED && user.isAdmin && limitReached && (
          <UpgradeBanner onClick={() => setShowUpgradeModal(true)} />
        )}
        {SUBSCRIPTION_ENABLED && (
          <UpgradeModal
            isOpen={showUpgradeModal}
            onClose={() => setShowUpgradeModal(false)}
            onUpgrade={() => {
              setShowUpgradeModal(false);
              history.push('/subscribe');
            }}
          />
        )}
        <PromptModal
          isOpen={isDeleteSongModalOpen}
          onClose={() => setIsDeleteSongModalOpen(false)}
          primaryText={t('songs.deleteSong')}
          message={
            modalDeleteSong
              ? `${modalDeleteSong.title}${modalDeleteSong.artist ? ` - ${modalDeleteSong.artist}` : ''}`
              : ''
          }
          onPrimaryAction={deleteSong}
          isDestructive={true}
        />
        <PromptModal
          isOpen={isDeleteServiceModalOpen}
          onClose={() => setIsDeleteServiceModalOpen(false)}
          primaryText={t('services.deleteService')}
          message={modalDeleteService?.title || ''}
          onPrimaryAction={deleteService}
          isDestructive={true}
        />

        <PromptModal
          isOpen={Boolean(serviceToEditWithSuggestions)}
          onClose={() => setServiceToEditWithSuggestions(null)}
          primaryText={t('services.emptyFirst')}
          message={
            serviceToEditWithSuggestions
              ? t('services.emptyFirstMessage', {
                  title: serviceToEditWithSuggestions.title,
                  count: serviceToEditWithSuggestions.songsIds.length,
                })
              : ''
          }
          onPrimaryAction={() => editServiceWithSuggestions(serviceToEditWithSuggestions, true)}
          secondaryText={t('services.addToEnd')}
          onSecondaryAction={() => editServiceWithSuggestions(serviceToEditWithSuggestions, false)}
          isDestructive={true}
        />

        <Modal
          isOpen={showSuggestModal}
          onClose={handleCloseSuggestModal}
          title={t('songs.suggestTitle', { count: suggestedSongs?.length })}
          className="suggest-songs-modal-container"
        >
          <SuggestSongsModal
            suggestedSongs={suggestedSongs}
            onClearSongs={handleClearSuggestedSongs}
            onSongReorder={handleSuggestedSongsReorder}
          />
        </Modal>

        <div className="mg-row">
          <div className="mg-col-md-12">
            <div className="mg-row">
              <div className="mg-col-lg-4">
                <Services
                  loading={loadingServices}
                  songs={songs}
                  services={services}
                  editServiceId={currentServiceForm.editServiceId}
                  onEditService={handleEditService}
                  onDeleteService={handleDeleteService}
                  onViewService={handleViewService}
                  hide={hideServicesContainer}
                />
              </div>

              <div className="mg-col-lg-8">
                {!loadingSongs && (
                  <>
                    <SearchBox
                      value={searchQuery}
                      label={t('songs.searchLabel', { count: totalCount })}
                      onChange={handleSearch}
                      onClearInput={handleClearInput}
                      isMainSearchBox={true}
                      hideServices={hideServices}
                      bar
                      count={
                        <span aria-label={t('songs.count', { count: totalCount })}>
                          <Icon icon={<MusicIcon />} />
                          {totalCount}
                        </span>
                      }
                      onRestore={hideServicesContainer ? handleShowServicesIconClick : null}
                      trailing={
                        <button
                          type="button"
                          className={classNames('filter-btn', { 'filter-btn--active': activeFilterCount > 0 })}
                          onClick={(e) => {
                            // The search box's own click would hide the services
                            e.stopPropagation();
                            setShowFilterModal(true);
                          }}
                          aria-label={t('songs.filterAndSort')}
                        >
                          <Icon icon={<FilterIcon />} />
                          {activeFilterCount > 0 && <span className="filter-count">{activeFilterCount}</span>}
                        </button>
                      }
                    />

                    <div className="mg-mb-2">
                      <TagGroup
                        name="categories"
                        tags={localizedCategories}
                        selectedTagId={categoryId}
                        onTagClick={handleCategorySelect}
                      />
                    </div>

                    <SongFiltersModal
                      isOpen={showFilterModal}
                      onClose={() => setShowFilterModal(false)}
                      sort={popularitySort}
                      onSortChange={handlePopularitySort}
                      keyOptions={keyFilterOptions}
                      selectedKeys={selectedKeys}
                      onKeysChange={handleKeysFilter}
                      timeSignatureOptions={timeSignatureOptions}
                      selectedTimeSignatures={selectedTimeSignatures}
                      onTimeSignaturesChange={handleTimeSignaturesFilter}
                      themeOptions={themeOptions}
                      selectedThemes={selectedThemes}
                      onThemesChange={handleThemesFilter}
                      chartOptions={chartOptions}
                      selectedCharts={selectedCharts}
                      onChartsChange={handleChartsFilter}
                      serviceNames={serviceNameOptions}
                      excludedServiceNames={activeExcludedServiceNames}
                      onExcludedServiceNamesChange={handleExcludedServiceNames}
                      onReset={handleResetFilters}
                      resultCount={totalCount}
                      accentColor={categoryAccent}
                    />
                  </>
                )}
                <SongList
                  songs={pagedSongs}
                  popularityData={popularityData}
                  popularitySort={popularitySort}
                  onFav={handleFavorite}
                  onDel={handleDelete}
                  onEdit={handleEditSong}
                  onAddToService={handleAddToService}
                  onAddToSuggested={handleAddToSuggested}
                  suggestedSongs={suggestedSongs}
                  loading={loadingSongs}
                  activeEditingServiceId={currentServiceForm.editServiceId}
                  isCreatingService={currentServiceForm.songs.length > 0}
                  songsInServiceIds={songsInServiceIds}
                  highlightedSongId={highlightedSongId}
                  accentColor={categoryAccent}
                  servicePlays={servicePlays}
                  tickedServiceLabels={tickedServiceLabels}
                  showChartProgress={selectedCharts.length > 0}
                />
                {totalCount >= 20 && (
                  <LoadMoreFooter
                    remainingSongs={remainingSongs}
                    displayedCount={displayedCount}
                    onLoadMore={handleLoadMore}
                    onScrollToTop={handleScrollToTop}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Main;
